import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  Share2,
  Copy,
  Link2,
  Code,
  Eye,
  Lock,
  Unlock,
  GitFork,
  Loader2,
  CheckCircle,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Share Dialog
 * 
 * Share machines publicly and embed in documentation:
 * - Generate shareable public URL
 * - Read-only viewer mode
 * - Embeddable iframe widget
 * - Optional password protection
 */

const ShareDialog = ({ open, onOpenChange, machineId, machineName }) => {
  const [loading, setLoading] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [settings, setSettings] = useState({
    is_public: true,
    password: '',
    allow_fork: false,
    allow_embed: true,
    show_code: false
  });
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState(false);

  const loadShareData = async () => {
    if (!machineId) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/machines/${machineId}/share`, {
        credentials: 'include'
      });
      const data = await response.json();
      
      if (data.shared) {
        setShareData(data);
        setSettings({
          is_public: data.is_public,
          password: '',
          allow_fork: data.allow_fork,
          allow_embed: data.allow_embed,
          show_code: data.show_code
        });
      }
    } catch (error) {
      console.error('Failed to load share data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadShareData();
    }
  }, [open, machineId]);

  const handleCreateShare = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/machines/${machineId}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        const data = await response.json();
        setShareData(data);
        toast.success('Share link created');
      } else {
        throw new Error('Failed to create share link');
      }
    } catch (error) {
      toast.error('Failed to create share link');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateShare = async () => {
    if (!shareData?.share_id) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/share/${shareData.share_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('Share settings updated');
        loadShareData();
      } else {
        throw new Error('Failed to update share settings');
      }
    } catch (error) {
      toast.error('Failed to update share settings');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteShare = async () => {
    if (!shareData?.share_id) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/share/${shareData.share_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setShareData(null);
        toast.success('Share link removed');
      } else {
        throw new Error('Failed to remove share link');
      }
    } catch (error) {
      toast.error('Failed to remove share link');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'url') {
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
      } else {
        setCopiedEmbed(true);
        setTimeout(() => setCopiedEmbed(false), 2000);
      }
      toast.success('Copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-[#0a0e27] border-white/10">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Share2 className="w-5 h-5 text-blue-400" />
            <span>Share Machine</span>
          </DialogTitle>
          <DialogDescription>
            Share "{machineName}" publicly or embed it in your documentation.
          </DialogDescription>
        </DialogHeader>

        {loading && !shareData ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
          </div>
        ) : shareData ? (
          <div className="space-y-6 py-4">
            {/* Share URL */}
            <div>
              <label className="text-xs text-gray-400 flex items-center space-x-2">
                <Link2 className="w-3 h-3" />
                <span>Share Link</span>
                {shareData.is_public && (
                  <Badge variant="outline" className="text-[9px] h-4 text-green-400 border-green-500/30">
                    Public
                  </Badge>
                )}
              </label>
              <div className="flex mt-2">
                <Input
                  value={shareData.url}
                  readOnly
                  className="rounded-r-none bg-black/30 border-white/10"
                />
                <Button
                  className="rounded-l-none"
                  onClick={() => copyToClipboard(shareData.url, 'url')}
                >
                  {copiedUrl ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-500">
                  {shareData.view_count} view{shareData.view_count !== 1 ? 's' : ''}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 text-xs"
                  onClick={() => window.open(shareData.url, '_blank')}
                >
                  <ExternalLink className="w-3 h-3 mr-1" />
                  Preview
                </Button>
              </div>
            </div>

            {/* Embed Code */}
            {shareData.allow_embed && (
              <div>
                <label className="text-xs text-gray-400 flex items-center space-x-2">
                  <Code className="w-3 h-3" />
                  <span>Embed Code</span>
                </label>
                <div className="relative mt-2">
                  <pre className="bg-black/30 border border-white/10 rounded p-3 text-xs overflow-x-auto">
                    <code className="text-blue-300">{shareData.embed_code}</code>
                  </pre>
                  <Button
                    size="sm"
                    className="absolute top-2 right-2 h-6"
                    onClick={() => copyToClipboard(shareData.embed_code, 'embed')}
                  >
                    {copiedEmbed ? <CheckCircle className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  </Button>
                </div>
              </div>
            )}

            {/* Settings */}
            <div className="space-y-4 pt-4 border-t border-white/10">
              <h4 className="text-sm font-medium">Share Settings</h4>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {settings.is_public ? <Unlock className="w-4 h-4 text-green-400" /> : <Lock className="w-4 h-4 text-amber-400" />}
                  <span className="text-sm">Public Access</span>
                </div>
                <Switch
                  checked={settings.is_public}
                  onCheckedChange={(checked) => setSettings(s => ({ ...s, is_public: checked }))}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Code className="w-4 h-4 text-gray-400" />
                  <span className="text-sm">Allow Embedding</span>
                </div>
                <Switch
                  checked={settings.allow_embed}
                  onCheckedChange={(checked) => setSettings(s => ({ ...s, allow_embed: checked }))}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Eye className="w-4 h-4 text-gray-400" />
                  <span className="text-sm">Show DSL Code</span>
                </div>
                <Switch
                  checked={settings.show_code}
                  onCheckedChange={(checked) => setSettings(s => ({ ...s, show_code: checked }))}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <GitFork className="w-4 h-4 text-gray-400" />
                  <span className="text-sm">Allow Forking</span>
                </div>
                <Switch
                  checked={settings.allow_fork}
                  onCheckedChange={(checked) => setSettings(s => ({ ...s, allow_fork: checked }))}
                />
              </div>

              <div className="flex space-x-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleUpdateShare}
                  disabled={loading}
                >
                  Update Settings
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteShare}
                  disabled={loading}
                >
                  Remove Share
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-8 text-center">
            <Share2 className="w-12 h-12 mx-auto mb-4 text-gray-600" />
            <p className="text-gray-400 mb-4">
              This machine is not shared yet.
            </p>
            <Button
              onClick={handleCreateShare}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Share2 className="w-4 h-4 mr-2" />
              )}
              Create Share Link
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareDialog;
